"use client";

import { motion } from "framer-motion";
import { CheckCircle2, Clock, Home, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

// Define interface for Masterkey facts
export interface MasterkeyFact {
  fact: string;
  emoji: string;
  category: "buyers" | "financing" | "negotiation" | "transactions" | "tools";
}

// Create default Masterkey facts
const defaultMasterkeyFacts: MasterkeyFact[] = [
  {
    fact: "Did you know? Masterkey is like your personal property assistant, helping you organize, analyze, and reimagine your dream home all in one place! ✨",
    emoji: "🎯",
    category: "tools",
  },
  {
    fact: "Want to see that dated kitchen as a modern masterpiece? Just describe your vision and our AI will show you how any space could look. Magic, right? ✨",
    emoji: "🪄",
    category: "tools",
  },
  {
    fact: "House hunting is better together! Invite your co-buyer to share properties, documents, and make decisions together. It's like a shared adventure! 🤝",
    emoji: "👥",
    category: "tools",
  },
];

interface PropertyData {
  property?: {
    address?: string;
    city?: string;
    state?: string;
    price?: number;
    bedrooms?: number;
    bathrooms?: number;
    square_feet?: number;
  };
  status?: string;
}

interface MarketData {
  marketData?: {
    trends?: {
      averageDaysOnMarket?: number;
      yearOverYearSalePrice?: number;
      numHomesOnMarket?: number;
      numHomesSold?: number;
    };
    homePrices?: Array<{
      label?: string;
      value?: string;
      aggregateData?: Array<{
        value?: string;
        date?: string;
        yoy?: string;
      }>;
    }>;
    demand?: {
      score?: number;
      level?: "low" | "medium" | "high";
      trend?: "decreasing" | "stable" | "increasing";
      metrics?: {
        daysOnMarket?: number;
        priceDrops?: number;
        viewsPerDay?: number;
        savesPerDay?: number;
      };
    };
    walkScore?: {
      score?: number;
      description?: string;
      amenities?: Array<{
        type: string;
        count: number;
        distance: string;
      }>;
    };
    climateData?: {
      risks?: Array<{
        type: string;
        level: "low" | "medium" | "high";
        probability: number;
        description: string;
      }>;
      averageTemperature?: number;
      annualPrecipitation?: number;
      naturalDisasterRisk?: {
        overall: "low" | "medium" | "high";
        specific: Record<string, "low" | "medium" | "high">;
      };
    };
  };
}

interface ValueData {
  estimated_value?: string | number;
}

interface ScoreData {
  home_condition_score?: number;
}

interface ImageAnalysisData {
  prosCount?: number;
  consCount?: number;
}

interface StreamUpdate {
  phase: string;
  data:
    | PropertyData
    | MarketData
    | ValueData
    | ScoreData
    | ImageAnalysisData
    | unknown;
  progress?: number;
  error?: string;
}

interface PropertyInfo {
  address?: string | null;
  city?: string;
  state?: string;
  price?: number;
  pricePerSqft?: number;
  beds?: number;
  baths?: number;
  sqft?: number;
  estimatedValue?: string | number;
  daysOnMarket?: number;
  condition?: number;
  pros?: number;
  cons?: number;
  yearBuilt?: number;
  propertyType?: string;
}

interface StreamingInsightsProps {
  streamUpdates: StreamUpdate[];
  currentPhase?: string;
  isLightAnalysis?: boolean;
  propertyInfo?: PropertyInfo;
}

function formatPrice(price?: number | string) {
  if (!price) return null;
  const numericPrice =
    typeof price === "string" ? Number.parseFloat(price) : price;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericPrice);
}

export function StreamingInsights({
  streamUpdates,
  currentPhase,
  isLightAnalysis = false,
  propertyInfo: initialPropertyInfo,
}: StreamingInsightsProps) {
  const [showFacts, setShowFacts] = useState(true);
  // Make the intial current fact index random between 0 and length - 1
  const [currentFactIndex, setCurrentFactIndex] = useState(
    Math.floor(Math.random() * defaultMasterkeyFacts.length),
  );
  const [hasError, setHasError] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState<PropertyInfo>(
    initialPropertyInfo || {},
  );
  const [showPropertyCard, setShowPropertyCard] = useState(false);
  const [negotiationInfo, setNegotiationInfo] = useState<{
    recommendedOffer?: number;
    successProbability?: number;
  }>({});
  const [marketInsights, setMarketInsights] = useState<{
    trend?: string;
    comparison?: string;
    daysOnMarket?: number;
  }>({});
  const [scores, setScores] = useState<{
    masterkey?: number;
    value?: number;
    location?: number;
    condition?: number;
  }>({});

  // Rotate through facts every 3 seconds
  useEffect(() => {
    if (!showFacts || showPropertyCard) return;

    const rotationInterval = setInterval(() => {
      setCurrentFactIndex((prevIndex) =>
        prevIndex === defaultMasterkeyFacts.length - 1 ? 0 : prevIndex + 1,
      );
    }, 4000);

    return () => clearInterval(rotationInterval);
  }, [showFacts, showPropertyCard]);

  // Timer to show property card after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPropertyCard(true);
      setShowFacts(false);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  // Extract property insights from stream updates
  useEffect(() => {
    try {
      if (Array.isArray(streamUpdates) && streamUpdates.length > 0) {
        let hasPropertyData = false;

        // Process updates to extract property information
        setPropertyInfo((prevInfo) => {
          const newPropertyInfo: PropertyInfo = { ...prevInfo };

          streamUpdates.forEach((update) => {
            if (!update || typeof update !== "object" || !update.phase) return;

            // Check for errors
            if (update.error || update.phase === "error") {
              setHasError(true);
              return;
            }

            // Extract property information from various phases
            if (
              update.phase === "property_data" ||
              update.phase === "property_details" ||
              update.phase === "scraping"
            ) {
              const propertyData = update.data as PropertyData;
              if (
                propertyData?.property ||
                propertyData?.status === "complete"
              ) {
                const property = propertyData.property || {};
                hasPropertyData = true;

                if (property.address)
                  newPropertyInfo.address = property.address;
                if (property.city) newPropertyInfo.city = property.city;
                if (property.state) newPropertyInfo.state = property.state;
                if (property.price) newPropertyInfo.price = property.price;
                if (property.bedrooms) newPropertyInfo.beds = property.bedrooms;
                if (property.bathrooms)
                  newPropertyInfo.baths = property.bathrooms;
                if (property.square_feet)
                  newPropertyInfo.sqft = property.square_feet;
              }
            }

            // Extract market data
            else if (update.phase === "market_data") {
              const marketData = update.data as MarketData & {
                _insights?: {
                  marketTrend?: string;
                  priceComparison?: string;
                };
              };

              if (marketData) {
                // Set days on market from market data
                if (marketData.marketData?.trends?.averageDaysOnMarket) {
                  newPropertyInfo.daysOnMarket =
                    marketData.marketData.trends.averageDaysOnMarket;
                }

                // Update market insights
                if (marketData._insights) {
                  setMarketInsights((prev) => ({
                    ...prev,
                    trend:
                      marketData._insights?.marketTrend ||
                      "Analyzing market trends...",
                    comparison:
                      marketData._insights?.priceComparison ||
                      "Analyzing price comparison...",
                    daysOnMarket:
                      marketData.marketData?.trends?.averageDaysOnMarket,
                  }));
                }
              }
            }

            // Extract negotiation data
            else if (update.phase === "negotiation_data") {
              const data = update.data as {
                recommendedOffer?: number;
                successProbability?: number;
                quickTake?: string;
                keyHighlights?: string[];
                potentialIssues?: string[];
              };

              if (data?.recommendedOffer) {
                setNegotiationInfo((prev) => ({
                  ...prev,
                  recommendedOffer: data.recommendedOffer,
                  successProbability: data.successProbability,
                }));
              }

              // Handle light analysis insights
              if (
                data?.quickTake ||
                data?.keyHighlights ||
                data?.potentialIssues
              ) {
                setMarketInsights((prev) => ({
                  ...prev,
                  trend:
                    data.quickTake || prev.trend || "Analysis in progress...",
                }));
              }
            }

            // Extract condition score
            else if (update.phase === "scores") {
              const scoreData = update.data as ScoreData & {
                value_score?: number;
                location_score?: number;
                masterkey_score?: number;
                home_condition_score?: number;
                condition_score?: number;
              };

              if (
                scoreData?.home_condition_score ||
                scoreData?.condition_score
              ) {
                newPropertyInfo.condition =
                  scoreData.home_condition_score || scoreData.condition_score;
              }

              // Update scores
              setScores((prev) => ({
                ...prev,
                condition:
                  scoreData.home_condition_score || scoreData.condition_score,
                value: scoreData.value_score,
                location: scoreData.location_score,
                masterkey: scoreData.masterkey_score,
              }));
            }

            // Extract pros and cons
            else if (update.phase === "image_analysis") {
              const imageData = update.data as ImageAnalysisData;
              if (imageData?.prosCount)
                newPropertyInfo.pros = imageData.prosCount;
              if (imageData?.consCount)
                newPropertyInfo.cons = imageData.consCount;
            }
          });

          // If we have any property data, we can stop showing facts
          if (hasPropertyData) {
            setShowFacts(false);
          }

          return newPropertyInfo;
        });
      }
    } catch (error) {
      console.error("Error processing stream updates:", error);
      setHasError(true);
    }
  }, [streamUpdates]);

  // Calculate price per square foot
  useEffect(() => {
    if (propertyInfo?.price && propertyInfo?.sqft) {
      setPropertyInfo((prev) => ({
        ...prev,
        pricePerSqft: Math.round(prev.price! / prev.sqft!),
      }));
    }
  }, [propertyInfo?.price, propertyInfo?.sqft]);

  return (
    <div className="space-y-3">
      {/* Masterkey Facts - Show for first 5 seconds */}
      {showFacts && !showPropertyCard && (
        <motion.div
          key={`fact-${currentFactIndex}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col items-center text-center space-y-3 py-4"
        >
          <div className="text-3xl">
            {defaultMasterkeyFacts[currentFactIndex].emoji}
          </div>
          <p className="text-sm leading-relaxed text-gray-700 max-w-md">
            {defaultMasterkeyFacts[currentFactIndex].fact}
          </p>
          <div className="inline-block px-3 py-1 text-xs font-medium tracking-wide uppercase text-primary/70">
            {defaultMasterkeyFacts[currentFactIndex].category}
          </div>
        </motion.div>
      )}

      {/* Property Card - Show after 5 seconds or when we have property info */}
      {(showPropertyCard || !showFacts) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-lg border border-gray-200 overflow-hidden"
        >
          {/* Property Details */}
          <div className="p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {propertyInfo?.address || (
                    <div className="h-5 bg-gray-100 rounded w-56 animate-pulse" />
                  )}
                </h3>
                <p className="text-xs text-gray-500">
                  {propertyInfo?.city && propertyInfo?.state ? (
                    `${propertyInfo.city}, ${propertyInfo.state}`
                  ) : (
                    <div className="h-4 bg-gray-100 rounded w-40 animate-pulse mt-1" />
                  )}
                </p>
              </div>
              <div className="text-right">
                <div className="text-base font-semibold">
                  {formatPrice(propertyInfo?.price) || (
                    <div className="h-5 bg-gray-100 rounded w-24 animate-pulse" />
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {propertyInfo?.pricePerSqft ? (
                    `$${propertyInfo.pricePerSqft}/sqft`
                  ) : (
                    <div className="h-3 bg-gray-100 rounded w-14 animate-pulse mt-1" />
                  )}
                </div>
              </div>
            </div>

            {/* Property stats + Pros/Cons in horizontal layout */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <Home className="w-4 h-4 text-gray-400" />
                  {propertyInfo?.beds !== undefined ? (
                    <span className="text-sm">{propertyInfo.beds} Beds</span>
                  ) : (
                    <div className="h-4 bg-gray-100 rounded w-14 animate-pulse" />
                  )}
                </div>

                {propertyInfo?.baths !== undefined ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">{propertyInfo.baths} Baths</span>
                  </div>
                ) : (
                  <div className="h-4 bg-gray-100 rounded w-14 animate-pulse" />
                )}

                {propertyInfo?.sqft !== undefined ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">
                      {propertyInfo.sqft.toLocaleString()} sqft
                    </span>
                  </div>
                ) : (
                  <div className="h-4 bg-gray-100 rounded w-20 animate-pulse" />
                )}
              </div>

              {/* Pros/Cons compact display */}
              {(propertyInfo?.pros || propertyInfo?.cons) && (
                <div className="flex items-center gap-2">
                  {propertyInfo.pros && (
                    <div className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {propertyInfo.pros}
                    </div>
                  )}

                  {propertyInfo.cons && (
                    <div className="flex items-center gap-1 text-red-500 text-xs font-semibold">
                      <XCircle className="w-3.5 h-3.5" />
                      {propertyInfo.cons}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Market Insights */}
            {propertyInfo.daysOnMarket && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-2 border-t border-gray-100"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-600">
                    Market Insights
                  </span>
                  <span className="text-xs py-1 px-2 bg-blue-50 rounded-full text-blue-700">
                    Avg. {propertyInfo.daysOnMarket} days on market
                  </span>
                </div>

                {marketInsights.trend && (
                  <p className="text-xs text-gray-600 mb-1">
                    {marketInsights.trend}
                  </p>
                )}

                {marketInsights.comparison && (
                  <p className="text-xs text-gray-600">
                    {marketInsights.comparison}
                  </p>
                )}
              </motion.div>
            )}

            {/* Recommended Offer - Show when available */}
            {negotiationInfo.recommendedOffer && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-3 border-t border-gray-100"
              >
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600">
                      Recommended Offer
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-blue-600">
                      {formatPrice(negotiationInfo.recommendedOffer)}
                    </span>
                    {propertyInfo.price && negotiationInfo.recommendedOffer && (
                      <span className="text-xs text-gray-500">
                        (
                        {new Intl.NumberFormat("en-US", {
                          maximumFractionDigits: 1,
                          minimumFractionDigits: 0,
                        }).format(
                          (negotiationInfo.recommendedOffer /
                            propertyInfo.price) *
                            100,
                        )}
                        % of list price)
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Scores in a compact display */}
            {scores.masterkey && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-2 border-t border-gray-100"
              >
                <div className="grid grid-cols-4 gap-1">
                  <div className="flex flex-col items-center">
                    <div className="bg-blue-600 text-white text-xs font-semibold w-8 h-8 flex items-center justify-center rounded-full">
                      {scores.masterkey}
                    </div>
                    <span className="text-[10px] mt-0.5 text-gray-500">
                      Overall
                    </span>
                  </div>

                  {scores.value && (
                    <div className="flex flex-col items-center">
                      <div className="bg-green-100 text-green-800 text-xs font-semibold w-8 h-8 flex items-center justify-center rounded-full">
                        {scores.value}
                      </div>
                      <span className="text-[10px] mt-0.5 text-gray-500">
                        Value
                      </span>
                    </div>
                  )}

                  {scores.location && (
                    <div className="flex flex-col items-center">
                      <div className="bg-blue-100 text-blue-800 text-xs font-semibold w-8 h-8 flex items-center justify-center rounded-full">
                        {scores.location}
                      </div>
                      <span className="text-[10px] mt-0.5 text-gray-500">
                        Location
                      </span>
                    </div>
                  )}

                  {scores.condition && (
                    <div className="flex flex-col items-center">
                      <div className="bg-purple-100 text-purple-800 text-xs font-semibold w-8 h-8 flex items-center justify-center rounded-full">
                        {scores.condition}
                      </div>
                      <span className="text-[10px] mt-0.5 text-gray-500">
                        Condition
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      )}

      {/* Keep tab open message */}
      <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Keep this tab open while we prepare your analysis.
        </p>
      </div>
    </div>
  );
}
